import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Volume2, Trash2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SavedWordsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const synthRef = useRef(window.speechSynthesis);

  useEffect(() => {
    fetchWords();
  }, []);

  const fetchWords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vocabulary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWords(response.data.words || []);
    } catch (error) {
      toast.error('Falha ao carregar vocabulário');
    } finally {
      setLoading(false);
    }
  };

  const getLanguageCode = (language) => {
    const codes = {
      'English': 'en-US',
      'Spanish': 'es-ES',
      'Portuguese': 'pt-BR',
      'French': 'fr-FR',
      'German': 'de-DE',
      'Chinese': 'zh-CN'
    };
    return codes[language] || 'en-US';
  };

  const selectBestVoice = (langCode, preferredGender = 'female') => {
    const voices = synthRef.current.getVoices();
    const language = langCode.split('-')[0];
    
    const languageVoices = voices.filter(voice => 
      voice.lang.startsWith(language) || voice.lang.startsWith(langCode)
    );
    
    if (languageVoices.length === 0) return null;
    
    const premiumKeywords = ['premium', 'enhanced', 'natural', 'neural'];
    const genderNames = preferredGender === 'male' 
      ? ['daniel', 'david', 'james', 'thomas', 'jorge', 'diego', 'hans', 'male']
      : ['samantha', 'karen', 'kate', 'victoria', 'monica', 'anna', 'female'];
    
    let bestVoice = languageVoices.find(voice => {
      const nameLower = voice.name.toLowerCase();
      return genderNames.some(name => nameLower.includes(name)) && 
             premiumKeywords.some(kw => nameLower.includes(kw));
    });
    
    if (!bestVoice) {
      bestVoice = languageVoices.find(voice => 
        genderNames.some(name => voice.name.toLowerCase().includes(name))
      );
    }
    
    return bestVoice || languageVoices[0];
  };

  const speakWord = (word, language, wordId) => {
    synthRef.current.cancel();
    
    // REGRA GLOBAL: Falar APENAS no idioma estudado, NUNCA a tradução
    let textToSpeak = word;
    
    // Remove translation in parentheses
    textToSpeak = textToSpeak.replace(/\([^)]*(?:Oi|Olá|E aí|Como|Que|Sobre|Você|Eu|Muito|Tudo|Legal|Bom|Boa|Isso)[^)]*\)/gi, '');
    textToSpeak = textToSpeak.replace(/\n*\(?(?:Tradução|PORTUGUÊS|PORTUGUESE|PT)[:\s]*[^)\n]+\)?/gi, '');
    
    // For Chinese: extract only Chinese text
    if (language === 'Chinese' && word.includes('CHINESE:')) {
      const chineseMatch = word.match(/CHINESE:\s*([\s\S]*?)(?=\n\n(?:PINYIN|PORTUGUÊS|PORTUGUESE)|$)/i);
      if (chineseMatch && chineseMatch[1]) {
        textToSpeak = chineseMatch[1].trim();
      }
    }
    
    textToSpeak = textToSpeak.trim();
    if (!textToSpeak) return;
    
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.lang = getLanguageCode(language);
    
    // Find best voice
    const voice = selectBestVoice(getLanguageCode(language), 'female');
    if (voice) {
      utterance.voice = voice;
    }
    
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    
    utterance.onstart = () => setPlayingId(wordId);
    utterance.onend = () => setPlayingId(null);
    utterance.onerror = () => setPlayingId(null);
    
    synthRef.current.speak(utterance);
  };

  const deleteWord = async (wordId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/vocabulary/${wordId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWords(words.filter(w => w.id !== wordId));
      toast.success('Palavra removida');
    } catch (error) {
      toast.error('Falha ao remover palavra');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short'
    });
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="saved-words-page">
      {/* Header */}
      <header className="bg-white shadow-soft sticky top-0 z-10">
        <div className="container mx-auto max-w-4xl px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-lime rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-forest" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-forest">Palavras Salvas</h1>
                <p className="text-sm text-stone">{words.length} palavras salvas</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto max-w-4xl px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-forest">Carregando...</div>
          </div>
        ) : words.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 bg-lime/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-lime" />
            </div>
            <h2 className="text-xl font-medium text-forest mb-2">Nenhuma palavra salva ainda</h2>
            <p className="text-stone mb-6">
              Clique no ícone de estrela nas mensagens da IA durante as conversas para salvar palavras.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-forest hover:bg-forest/90 text-sand"
            >
              Iniciar uma Conversa
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence>
              {words.map((word, index) => (
                <motion.div
                  key={word.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-4 shadow-soft flex items-center gap-4"
                  data-testid={`word-item-${index}`}
                >
                  {/* Word & Translation */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-forest truncate">{word.word}</p>
                    <p className="text-sm text-stone truncate">{word.translation}</p>
                  </div>

                  {/* Language Badge */}
                  <div className="hidden sm:block">
                    <span className="text-xs bg-lime/20 text-forest px-2 py-1 rounded-full">
                      {word.language}
                    </span>
                  </div>

                  {/* Date */}
                  <div className="hidden sm:block text-xs text-stone">
                    {formatDate(word.created_at)}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => speakWord(word.word, word.language, word.id)}
                      variant="ghost"
                      size="sm"
                      className={`rounded-full ${playingId === word.id ? 'bg-lime text-forest' : ''}`}
                      data-testid={`play-word-${index}`}
                    >
                      <Volume2 className={`w-4 h-4 ${playingId === word.id ? 'animate-pulse' : ''}`} />
                    </Button>
                    <Button
                      onClick={() => deleteWord(word.id)}
                      variant="ghost"
                      size="sm"
                      className="rounded-full text-clay hover:bg-clay/10"
                      data-testid={`delete-word-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
};

export default SavedWordsPage;
